import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { notify } from "../Notification";
import { contact } from "../../data/search";
import {
  HOME,
  go,
  hostOf,
  isReportFrom,
  navigated,
  newTab,
  openTab,
  reported,
  siteOf,
  stepped,
  urlOf,
} from "../../utils/browser";
import { AppShell, useKeyboardInset } from "./ios";
import pouchIcon from "../../assets/icons/chrome/usepouch.png";
import { ReactComponent as BackIcon } from "../../assets/svgs/sf-symbols/chrome-ios/arrow.left.svg";
import { ReactComponent as ForwardIcon } from "../../assets/svgs/sf-symbols/chrome-ios/arrow.right.svg";
import { ReactComponent as PlusIcon } from "../../assets/svgs/sf-symbols/chrome-ios/plus.svg";
import { ReactComponent as MoreIcon } from "../../assets/svgs/sf-symbols/chrome-ios/ellipsis.svg";
import { ReactComponent as ShareIcon } from "../../assets/svgs/sf-symbols/chrome-ios/square.and.arrow.up.svg";
import { ReactComponent as SparklesIcon } from "../../assets/svgs/sf-symbols/chrome-ios/sparkles-two.svg";

// A new tab: a search box (the address bar) and shortcuts to Sylvester's things.
const NewTabPage = ({ navigate, onSearch }) => {
  const shortcuts = [
    { label: "Use Pouch", icon: pouchIcon, run: () => navigate(HOME) },
    { label: "LinkedIn", letter: "in", run: () => openTab(contact("LinkedIn").href) },
    { label: "Email me", letter: "@", run: () => openTab(contact("Email").href) },
  ];
  return (
    <div className="mc-ntp">
      <button type="button" className="mc-ntp-search" onClick={onSearch}>
        Search or type URL
      </button>
      <div className="mc-ntp-shortcuts">
        {shortcuts.map((s) => (
          <button type="button" key={s.label} className="mc-ntp-tile" onClick={s.run}>
            <span className="mc-ntp-tile-icon">{s.icon ? <img src={s.icon} alt="" /> : s.letter}</span>
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// Chrome on Sylvester's iPhone: the page, with the address bar and toolbar along the bottom in the
// dark theme. It opens on Use Pouch. The tab button shows ":)" as his does: Chrome's sign for more
// than 99 open tabs.
const ChromeApp = ({ onClose }) => {
  const [tab, setTab] = useState(() => newTab(HOME));
  const [editing, setEditing] = useState(null); // the address bar's text while it's being edited
  const frame = useRef(null);
  const field = useRef(null);
  const keyboard = useKeyboardInset(editing !== null);
  const url = urlOf(tab);

  // Pages that report where they are: follow them in the address bar and history.
  useEffect(() => {
    const onMessage = (e) => setTab((t) => (isReportFrom(e, frame.current, t.src) ? reported(t, e.data) : t));
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const navigate = (to) => setTab((t) => navigated(t, to));
  const step = (delta) => {
    const next = stepped(tab, delta, frame.current);
    if (next !== tab) setTab(next);
  };

  // Focus the field within the tap itself: iOS Safari only raises the keyboard then. Once the
  // address is in it, it's all selected, so typing replaces it.
  const startEditing = () => {
    setEditing(url || "");
    if (field.current) field.current.focus({ preventScroll: true });
  };
  const isEditing = editing !== null;
  useLayoutEffect(() => {
    if (isEditing && field.current) field.current.select();
  }, [isEditing]);

  const share = async () => {
    if (!url) return;
    if (navigator.share) {
      navigator.share({ title: tab.title || siteOf(url).title, url }).catch(() => {});
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(url).catch(() => {});
      notify("Link copied.");
    }
  };

  return (
    <AppShell label="Chrome" origin="84% 93%" className="ios-app--chrome" onClose={onClose}>
      <div className="mc">
        <div className="mc-status" />
        <div className="mc-page">
          {tab.src ? (
            <iframe
              key={`${tab.id}-${tab.load}`}
              ref={frame}
              src={tab.src}
              title={tab.title || siteOf(tab.src).title}
              className="mc-frame"
            />
          ) : (
            <NewTabPage navigate={navigate} onSearch={startEditing} />
          )}
        </div>

        <div className={`mc-bar ${editing !== null ? "is-editing" : ""}`} style={{ "--keyboard": `${keyboard}px` }}>
          <form
            className="mc-omnibox"
            onSubmit={(e) => {
              e.preventDefault();
              go(editing || "", navigate);
              setEditing(null);
              field.current.blur();
            }}
          >
            <button
              type="button"
              className="mc-omnibox-tool"
              onClick={() => notify("Gemini works in Chrome itself, not in this replica.")}
              aria-label="Ask Gemini"
            >
              <SparklesIcon className="mc-sym-sparkles" />
            </button>
            <input
              ref={field}
              className="mc-address-field"
              value={editing !== null ? editing : ""}
              onChange={(e) => setEditing(e.target.value)}
              onBlur={() => setEditing(null)}
              placeholder="Search or type URL"
              aria-label="Address and search bar"
              enterKeyHint="go"
              inputMode="url"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
            />
            {editing === null && (
              <button type="button" className="mc-address" onClick={startEditing}>
                {url ? hostOf(url) : "Search or type URL"}
              </button>
            )}
            <button type="button" className="mc-omnibox-tool" onClick={share} disabled={!url} aria-label="Share">
              <ShareIcon className="mc-sym-share" />
            </button>
          </form>

          <div className="mc-toolbar">
            <button
              type="button"
              className="mc-tool"
              onClick={() => step(-1)}
              disabled={tab.index < 1}
              aria-label="Back"
            >
              <BackIcon className="mc-sym-arrow" />
            </button>
            <button
              type="button"
              className="mc-tool"
              onClick={() => step(1)}
              disabled={tab.index >= tab.history.length - 1}
              aria-label="Forward"
            >
              <ForwardIcon className="mc-sym-arrow" />
            </button>
            <button type="button" className="mc-tool mc-tool--new" onClick={() => navigate(null)} aria-label="New Tab">
              <PlusIcon className="mc-sym-plus" />
            </button>
            <button
              type="button"
              className="mc-tool"
              onClick={() => notify("That's my real Chrome's 100+ open tabs. This one keeps to one.")}
              aria-label="Show tabs"
            >
              <span className="mc-tabs" aria-hidden="true">
                :)
              </span>
            </button>
            <button
              type="button"
              className="mc-tool"
              onClick={() => notify("Chrome's menu works on my phone, not in this replica.")}
              aria-label="Menu"
            >
              <MoreIcon className="mc-sym-more" />
            </button>
          </div>
        </div>
      </div>
    </AppShell>
  );
};

export default ChromeApp;
